import { useEffect, useState } from "react";
import Navbar from "../../Navbar/NavbarComponent/Navbar";
import Loader from "../../../shared/components/Loader";
import SimpleButton from "../../../shared/components/Button/Button";
import ButtonLoader from "../../../shared/components/ButtonLoader";
import CustomModal from "../../../shared/components/CustomModal";
import TextBox from "../../../shared/components/TextBox";
import TextArea from "../../../shared/components/TextArea";
import Select from "../../../shared/components/Select";
import {
  BUTTON_LABEL,
  icons,
  LABELS,
  toastError,
  toastSuccess,
} from "../../../shared/constants";
import {
  WhatsAppPreviewResponse,
  WhatsAppStage,
  WhatsAppTemplate,
  useWhatsappStagesListQuery,
  useWhatsappTemplatesListQuery,
  useWhatsappTemplatesCreateMutation,
  useWhatsappTemplatesPreviewMutation,
  useWhatsappTemplatesUpdateMutation,
} from "../../../Features/whatsapp/whatsapp-slice";

interface ParamMapping {
  interaktVar: string;
  dbField: string;
}

const ALL_ORDER_VARIABLES: { [key: string]: { _id: string; name: string } } = {
  first_name: { _id: "first_name", name: "First Name" },
  last_name: { _id: "last_name", name: "Last Name" },
  order_number: { _id: "order_number", name: "Order Number" },
  delivery_date: { _id: "delivery_date", name: "Estimated Delivery Date" },
  order_date: { _id: "order_date", name: "Order Date" },
  store_name: { _id: "store_name", name: "Store Name" },
  brand_name: { _id: "brand_name", name: "Brand / Company Name" },
  contact_number: { _id: "contact_number", name: "Store Contact Number" },
  status: { _id: "status", name: "Order Status" },
  item_code: { _id: "item_code", name: "Item Code" },
  design_no: { _id: "design_no", name: "Design Number" },
  price: { _id: "price", name: "Total Price / Amount" },
  advance_amount: { _id: "advance_amount", name: "Advance Payment Amount" },
  confirmed_date: { _id: "confirmed_date", name: "Confirmation Date" },
};

const ORDER_VARIABLES = Object.values(ALL_ORDER_VARIABLES);

const STAGE_VARIABLES_MAP: Record<string, string[]> = {
  order_creation: [
    "first_name",
    "last_name",
    "order_number",
    "delivery_date",
    "order_date",
    "store_name",
    "brand_name",
    "item_code",
    "design_no",
    "price",
    "advance_amount",
    "status",
  ],
  order_under_processing: [
    "first_name",
    "last_name",
    "order_number",
    "delivery_date",
    "status",
    "confirmed_date",
    "store_name",
    "brand_name",
    "item_code",
    "design_no",
  ],
  warehouse_confirmation: [
    "first_name",
    "last_name",
    "order_number",
    "delivery_date",
    "status",
    "confirmed_date",
    "store_name",
    "brand_name",
  ],
  delivered_to_store: [
    "first_name",
    "last_name",
    "order_number",
    "delivery_date",
    "store_name",
    "contact_number",
    "brand_name",
    "status",
  ],
  customer_dispatch: [
    "first_name",
    "last_name",
    "order_number",
    "delivery_date",
    "contact_number",
    "store_name",
    "brand_name",
    "status",
  ],
  ready_for_dispatch: [
    "first_name",
    "last_name",
    "order_number",
    "delivery_date",
    "contact_number",
    "store_name",
    "brand_name",
    "status",
  ],
  bill_completion: [
    "first_name",
    "last_name",
    "order_number",
    "price",
    "order_date",
    "store_name",
    "brand_name",
    "status",
  ],
  production_start: [
    "first_name",
    "last_name",
    "order_number",
    "delivery_date",
    "status",
    "item_code",
    "design_no",
    "store_name",
    "brand_name",
  ],
  cutting_completed: [
    "first_name",
    "last_name",
    "order_number",
    "delivery_date",
    "status",
    "item_code",
    "design_no",
    "store_name",
    "brand_name",
  ],
  embroidery_completed: [
    "first_name",
    "last_name",
    "order_number",
    "delivery_date",
    "status",
    "item_code",
    "design_no",
    "store_name",
    "brand_name",
  ],
  stitching_completed: [
    "first_name",
    "last_name",
    "order_number",
    "delivery_date",
    "status",
    "item_code",
    "design_no",
    "store_name",
    "brand_name",
  ],
  finishing_completed: [
    "first_name",
    "last_name",
    "order_number",
    "delivery_date",
    "status",
    "item_code",
    "design_no",
    "store_name",
    "brand_name",
  ],
};

const getStageVariables = (stageKey?: string, currentDbField?: string) => {
  const normalizedKey = (stageKey || "").toLowerCase().trim();
  const keys = STAGE_VARIABLES_MAP[normalizedKey];
  const list =
    keys && keys.length > 0
      ? keys.map((k) => ALL_ORDER_VARIABLES[k]).filter(Boolean)
      : ORDER_VARIABLES;

  if (currentDbField && !list.some((item) => item._id === currentDbField)) {
    const existing = ALL_ORDER_VARIABLES[currentDbField] || {
      _id: currentDbField,
      name: currentDbField,
    };
    return [existing, ...list];
  }
  return list;
};

const DEFAULT_PARAM_SEQUENCE = [
  "first_name",
  "order_number",
  "delivery_date",
  "order_date",
  "store_name",
  "brand_name",
  "contact_number",
  "status",
];

const extractTemplateVariables = (body: string): string[] => {
  const vars: string[] = [];
  for (let i = 1; i <= 10; i++) {
    if (body && (body.includes(`{{${i}}}`) || body.includes(`{${i}}`))) {
      vars.push(String(i));
    }
  }
  return vars;
};

// WhatsApp template mapping (TSK-WA-013): per-stage MTM template content and
// Interakt template name/id, with a live preview render.
const WhatsAppTemplates = () => {
  const [templates, setTemplates] = useState<WhatsAppTemplate[]>([]);
  const [editing, setEditing] = useState<WhatsAppTemplate | undefined>();
  const [editForm, setEditForm] = useState<{
    stageName: string;
    templateBody: string;
    interaktTemplateName: string;
    interaktTemplateId: string;
    parameterOrder: string[];
  }>({
    stageName: "",
    templateBody: "",
    interaktTemplateName: "",
    interaktTemplateId: "",
    parameterOrder: [],
  });
  const [editMappings, setEditMappings] = useState<ParamMapping[]>([]);
  const [showEdit, setShowEdit] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState<{
    stageId: string;
    stageName: string;
    templateBody: string;
    interaktTemplateName: string;
    interaktTemplateId: string;
    parameterOrder: string[];
  }>({
    stageId: "",
    stageName: "",
    templateBody: "",
    interaktTemplateName: "",
    interaktTemplateId: "",
    parameterOrder: [],
  });
  const [addMappings, setAddMappings] = useState<ParamMapping[]>([
    { interaktVar: "{{1}}", dbField: "first_name" },
    { interaktVar: "{{2}}", dbField: "order_number" },
    { interaktVar: "{{3}}", dbField: "delivery_date" },
  ]);
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState<
    WhatsAppPreviewResponse | undefined
  >();

  const { data, isFetching, isSuccess, refetch } =
    useWhatsappTemplatesListQuery(undefined, {
      refetchOnMountOrArgChange: true,
    });

  const { data: stagesData, refetch: refetchStages } =
    useWhatsappStagesListQuery(undefined, {
      refetchOnMountOrArgChange: true,
    });

  const configuredStageKeys = new Set(
    (templates || [])
      .map((t) => (t.stageKey || "").toLowerCase().trim())
      .filter(Boolean),
  );
  const configuredStageLabels = new Set(
    (templates || [])
      .map((t) => (t.stageLabel || "").toLowerCase().trim())
      .filter(Boolean),
  );
  const configuredStageIds = new Set(
    (templates || [])
      .map((t) => String(t.stageId || "").trim())
      .filter(Boolean),
  );

  const stageOptions = (stagesData?.list || [])
    .filter((s: WhatsAppStage) => {
      if (s.hasTemplate) return false;
      if (s.stageId && configuredStageIds.has(String(s.stageId).trim()))
        return false;
      if (
        s.stageKey &&
        configuredStageKeys.has(s.stageKey.toLowerCase().trim())
      )
        return false;
      if (
        s.stageLabel &&
        configuredStageLabels.has(s.stageLabel.toLowerCase().trim())
      )
        return false;
      return true;
    })
    .map((s: WhatsAppStage) => ({
      _id: s.stageId,
      name: s.stageLabel,
    }));

  const [createTemplate, { isLoading: createLoading }] =
    useWhatsappTemplatesCreateMutation();
  const [updateTemplate, { isLoading: updateLoading }] =
    useWhatsappTemplatesUpdateMutation();
  const [previewTemplate, { isLoading: previewLoading }] =
    useWhatsappTemplatesPreviewMutation();

  useEffect(() => {
    if (isSuccess && data) {
      setTemplates(data.list);
    }
  }, [data, isSuccess]);

  const handleAddAddRow = () => {
    const nextVarNum = addMappings.length + 1;
    const newRow: ParamMapping = {
      interaktVar: `{{${nextVarNum}}}`,
      dbField: DEFAULT_PARAM_SEQUENCE[addMappings.length] || "first_name",
    };
    const next = [...addMappings, newRow];
    setAddMappings(next);
    setAddForm((prev) => ({
      ...prev,
      parameterOrder: next.map((m) => m.dbField),
    }));
  };

  const handleDeleteAddRow = (indexToDelete: number) => {
    if (addMappings.length <= 1) return;
    const next = addMappings.filter((_, idx) => idx !== indexToDelete);
    setAddMappings(next);
    setAddForm((prev) => ({
      ...prev,
      parameterOrder: next.map((m) => m.dbField),
    }));
  };

  const handleUpdateAddRow = (
    index: number,
    key: "interaktVar" | "dbField",
    value: string,
  ) => {
    const next = [...addMappings];
    next[index] = { ...next[index], [key]: value };
    setAddMappings(next);
    setAddForm((prev) => ({
      ...prev,
      parameterOrder: next.map((m) => m.dbField),
    }));
  };

  const handleAddEditRow = () => {
    const nextVarNum = editMappings.length + 1;
    const newRow: ParamMapping = {
      interaktVar: `{{${nextVarNum}}}`,
      dbField: DEFAULT_PARAM_SEQUENCE[editMappings.length] || "first_name",
    };
    const next = [...editMappings, newRow];
    setEditMappings(next);
    setEditForm((prev) => ({
      ...prev,
      parameterOrder: next.map((m) => m.dbField),
    }));
  };

  const handleDeleteEditRow = (indexToDelete: number) => {
    if (editMappings.length <= 1) return;
    const next = editMappings.filter((_, idx) => idx !== indexToDelete);
    setEditMappings(next);
    setEditForm((prev) => ({
      ...prev,
      parameterOrder: next.map((m) => m.dbField),
    }));
  };

  const handleUpdateEditRow = (
    index: number,
    key: "interaktVar" | "dbField",
    value: string,
  ) => {
    const next = [...editMappings];
    next[index] = { ...next[index], [key]: value };
    setEditMappings(next);
    setEditForm((prev) => ({
      ...prev,
      parameterOrder: next.map((m) => m.dbField),
    }));
  };

  const openAdd = () => {
    refetchStages();
    const initial: ParamMapping[] = [
      { interaktVar: "{{1}}", dbField: "first_name" },
      { interaktVar: "{{2}}", dbField: "order_number" },
      { interaktVar: "{{3}}", dbField: "delivery_date" },
    ];
    setAddMappings(initial);
    setAddForm({
      stageId: "",
      stageName: "",
      templateBody: "",
      interaktTemplateName: "",
      interaktTemplateId: "",
      parameterOrder: initial.map((m) => m.dbField),
    });
    setShowAdd(true);
  };

  const handleStageSelect = (e: any) => {
    const selectedId = e.target.value;
    const selectedStage = (stagesData?.list || []).find(
      (s: WhatsAppStage) => s.stageId === selectedId,
    );
    setAddForm({
      ...addForm,
      stageId: selectedId,
      stageName: selectedStage ? selectedStage.stageLabel : "",
    });
  };

  const handleAdd = async () => {
    if (!addForm.stageId && !addForm.stageName.trim()) {
      toastError("Please select a stage");
      return;
    }
    if (!addForm.templateBody.trim()) {
      toastError("Template body is required");
      return;
    }
    if (!addForm.interaktTemplateName.trim()) {
      toastError("Interakt template name is required");
      return;
    }

    const response = await createTemplate({
      data: {
        stageId: addForm.stageId || undefined,
        stageName: addForm.stageName,
        stageLabel: addForm.stageName,
        templateBody: addForm.templateBody,
        interaktTemplateName: addForm.interaktTemplateName,
        interaktTemplateId: addForm.interaktTemplateId,
        parameterOrder: addMappings.map((m) => m.dbField),
        variableMapping: addMappings,
      },
    })
      .unwrap()
      .catch((err: any) => {
        toastError(err.data?.errors?.[0]?.msg || "Failed to create template");
      });
    if (response && response.status) {
      toastSuccess(response.message);
      refetch();
      refetchStages();
      setShowAdd(false);
    }
  };

  const openEdit = (template: WhatsAppTemplate) => {
    setEditing(template);
    const existingOrder = template.parameterOrder || [];
    const templateVars = extractTemplateVariables(template.templateBody || "");
    let initial: ParamMapping[] = [];

    if (template.variableMapping && template.variableMapping.length > 0) {
      initial = template.variableMapping.map((m, idx) => ({
        interaktVar: m.interaktVar || `{{${idx + 1}}}`,
        dbField:
          m.dbField === "customer_name"
            ? "first_name"
            : m.dbField || DEFAULT_PARAM_SEQUENCE[idx] || "first_name",
      }));
    } else if (existingOrder.length > 0) {
      initial = existingOrder.map((field, idx) => ({
        interaktVar: `{{${idx + 1}}}`,
        dbField: field === "customer_name" ? "first_name" : field,
      }));
    } else if (templateVars.length > 0) {
      initial = templateVars.map((v, idx) => ({
        interaktVar: `{{${v}}}`,
        dbField: DEFAULT_PARAM_SEQUENCE[idx] || "first_name",
      }));
    } else {
      initial = [
        { interaktVar: "{{1}}", dbField: "first_name" },
        { interaktVar: "{{2}}", dbField: "order_number" },
        { interaktVar: "{{3}}", dbField: "delivery_date" },
      ];
    }

    setEditMappings(initial);
    setEditForm({
      stageName: template.stageLabel || "",
      templateBody: template.templateBody,
      interaktTemplateName: template.interaktTemplateName,
      interaktTemplateId: template.interaktTemplateId,
      parameterOrder: initial.map((m) => m.dbField),
    });
    setShowEdit(true);
  };

  const handleSave = async () => {
    if (!editing) return;
    const response = await updateTemplate({
      id: editing.templateId,
      data: {
        ...editForm,
        parameterOrder: editMappings.map((m) => m.dbField),
        variableMapping: editMappings,
      },
    })
      .unwrap()
      .catch((err: any) => {
        toastError(err.data?.errors?.[0]?.msg || "Failed to update template");
      });
    if (response && response.status) {
      toastSuccess(response.message);
      refetch();
      setShowEdit(false);
    }
  };

  const openPreview = async (template: WhatsAppTemplate) => {
    const response = await previewTemplate({ templateId: template.templateId })
      .unwrap()
      .catch((err: any) => {
        toastError(
          err.data?.errors?.[0]?.msg || LABELS.WHATSAPP_PREVIEW_FAILED,
        );
      });
    if (response && response.status) {
      setPreviewData(response);
      setShowPreview(true);
    }
  };

  return (
    <>
      <div className="full-height">
        <Navbar title={LABELS.WHATSAPP_TEMPLATES_TEXT} />
        <main className="main-content">
          <div className="pt-3">
            <div className="container-fluid">
              <div className="card custom-card mb-4">
                <div className="card-header border-bottom d-flex justify-content-between align-items-center">
                  <h5 className="mb-0 text-black font-weight-600">
                    {LABELS.WHATSAPP_TEMPLATES_TEXT}
                  </h5>
                  <SimpleButton
                    className="btn theme-btn theme-btn-sm w-auto"
                    btnIcon={icons.ICON_ADD}
                    btnIconClass={"me-1"}
                    onClick={openAdd}
                    text={BUTTON_LABEL.ADD}
                  />
                </div>
                <div className="card-body p-0">
                  {isFetching && !templates.length ? (
                    <Loader />
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-header-shadow mb-0">
                        <thead>
                          <tr>
                            <th>{LABELS.STAGE_NAME_TEXT}</th>
                            <th>{LABELS.INTERAKT_TEMPLATE_NAME_TEXT}</th>
                            <th>{LABELS.TEMPLATE_BODY_TEXT}</th>
                            <th>{LABELS.PLACEHOLDERS_TEXT}</th>
                            <th className="text-center">
                              {LABELS.ACTIONS_TEXT}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {templates.length > 0 ? (
                            templates.map((template) => (
                              <tr key={template.templateId}>
                                <td className="font-weight-600 text-dark">
                                  {template.stageLabel}
                                </td>
                                <td>
                                  {template.interaktTemplateName ? (
                                    <span className="badge bg-light text-dark border px-2 py-1">
                                      {template.interaktTemplateName}
                                    </span>
                                  ) : (
                                    <span className="text-muted fst-italic">
                                      -
                                    </span>
                                  )}
                                </td>
                                <td
                                  style={{
                                    maxWidth: "320px",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                  title={template.templateBody}
                                >
                                  {template.templateBody || "-"}
                                </td>
                                <td className="text-muted font-12">
                                  {template.placeholdersUsed?.length
                                    ? template.placeholdersUsed.join(", ")
                                    : "-"}
                                </td>
                                <td>
                                  <div className="d-flex justify-content-center gap-2">
                                    <SimpleButton
                                      className="btn theme-btn theme-btn-sm btn-inverse w-auto"
                                      btnIcon={icons.ICON_VIEW}
                                      btnIconClass={"me-1"}
                                      onClick={() => openPreview(template)}
                                      text={LABELS.PREVIEW_TEXT}
                                    />
                                    <SimpleButton
                                      className="btn theme-btn theme-btn-sm w-auto"
                                      btnIcon={icons.ICON_EDIT}
                                      btnIconClass={"me-1"}
                                      onClick={() => openEdit(template)}
                                      text={BUTTON_LABEL.EDIT}
                                    />
                                  </div>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td
                                colSpan={5}
                                className="text-center py-4 text-muted"
                              >
                                {LABELS.No_DATA_FOUND_TEXT}
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>

      <CustomModal
        show={showAdd}
        close={() => setShowAdd(false)}
        size={"lg"}
        message={""}
        modalTitle={`${BUTTON_LABEL.ADD} — ${LABELS.WHATSAPP_TEMPLATES_TEXT}`}
        modalButton={[
          {
            text: BUTTON_LABEL.CANCEL,
            action: () => setShowAdd(false),
            className: "btn theme-btn btn-inverse me-2",
          },
          {
            text: createLoading ? <ButtonLoader /> : BUTTON_LABEL.SAVE,
            action: handleAdd,
            className: "btn theme-btn",
            isLoading: createLoading,
          },
        ]}
        fullscreen={undefined}
        className={""}
        centerClass={""}
        modalTitleIcon={""}
      >
        <div className="form-group">
          <Select
            label={LABELS.STAGE_NAME_TEXT}
            id="addStageName"
            name="stageId"
            required={true}
            value={addForm.stageId}
            onChange={handleStageSelect}
            data={stageOptions}
            selectText="Select Stage"
          />
        </div>
        <div className="form-group mt-3">
          <TextArea
            label={LABELS.TEMPLATE_BODY_TEXT}
            id="addTemplateBody"
            name="templateBody"
            required={true}
            value={addForm.templateBody}
            onChange={(e: any) =>
              setAddForm({ ...addForm, templateBody: e.target.value })
            }
            maxLength={1000}
            style={{ height: "180px" }}
            rows={8}
          />
        </div>
        <div className="form-group mt-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <label className="font-weight-600 text-dark mb-0 font-14">
              Variable Mapping
            </label>
            <button
              type="button"
              className="btn theme-btn theme-btn-sm d-flex align-items-center gap-1 py-1 px-2 font-12"
              onClick={handleAddAddRow}
            >
              <i className="icon-add"></i> Add Variable
            </button>
          </div>

          <div className="p-3 border rounded bg-white shadow-sm">
            <div className="row g-2 mb-2 text-muted font-12 font-weight-600">
              <div className="col-4 d-flex align-items-center gap-1">
                <span>Interakt Variable</span>
                <span
                  title="Variable name must be the same as in the template body (e.g. {{1}}, {{2}})"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "16px",
                    height: "16px",
                    borderRadius: "50%",
                    backgroundColor: "#e9ecef",
                    color: "#495057",
                    fontSize: "11px",
                    fontWeight: "bold",
                    cursor: "help",
                  }}
                >
                  ℹ
                </span>
              </div>
              <div className="col-7">Database / Order Field</div>
              <div className="col-1 text-center">Action</div>
            </div>

            {addMappings.map((row, idx) => (
              <div key={idx} className="row g-2 align-items-center mb-2">
                <div className="col-4">
                  <input
                    type="text"
                    className="form-control"
                    style={{
                      backgroundColor: "#fff",
                      border: "1px solid #ced4da",
                      borderRadius: "4px",
                      height: "38px",
                      padding: "6px 12px",
                    }}
                    title="Variable name must be the same as in the template body (e.g. {{1}}, {{2}})"
                    placeholder={`{{${idx + 1}}}`}
                    value={row.interaktVar}
                    onChange={(e) =>
                      handleUpdateAddRow(idx, "interaktVar", e.target.value)
                    }
                  />
                </div>
                <div className="col-7">
                  <Select
                    id={`addParamSelect_${idx}`}
                    name={`addParamSelect_${idx}`}
                    value={row.dbField}
                    onChange={(e: any) =>
                      handleUpdateAddRow(idx, "dbField", e.target.value)
                    }
                    data={getStageVariables(
                      (stagesData?.list || []).find(
                        (s: WhatsAppStage) => s.stageId === addForm.stageId,
                      )?.stageKey,
                      row.dbField,
                    )}
                    selectText="Select DB Field"
                    className="form-control select-box"
                  />
                </div>
                <div className="col-1 text-center">
                  <button
                    type="button"
                    className="btn btn-outline-danger btn-sm d-flex align-items-center justify-content-center p-0 mx-auto"
                    style={{
                      width: "38px",
                      height: "38px",
                      borderRadius: "4px",
                    }}
                    title="Delete Variable"
                    onClick={() => handleDeleteAddRow(idx)}
                    disabled={addMappings.length <= 1}
                  >
                    <i className="icon-delete font-14"></i>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="form-group mt-3">
          <TextBox
            label={LABELS.INTERAKT_TEMPLATE_NAME_TEXT}
            id="addInteraktTemplateName"
            name="interaktTemplateName"
            required={true}
            value={addForm.interaktTemplateName}
            onChange={(e: any) =>
              setAddForm({ ...addForm, interaktTemplateName: e.target.value })
            }
          />
        </div>
        <div className="form-group mt-3">
          <TextBox
            label={`${LABELS.INTERAKT_TEMPLATE_NAME_TEXT} ID`}
            id="addInteraktTemplateId"
            name="interaktTemplateId"
            value={addForm.interaktTemplateId}
            onChange={(e: any) =>
              setAddForm({ ...addForm, interaktTemplateId: e.target.value })
            }
          />
        </div>
      </CustomModal>

      <CustomModal
        show={showEdit}
        close={() => setShowEdit(false)}
        size={"lg"}
        message={""}
        modalTitle={`${BUTTON_LABEL.EDIT} — ${
          editing?.stageLabel || LABELS.WHATSAPP_TEMPLATES_TEXT
        }`}
        modalButton={[
          {
            text: BUTTON_LABEL.CANCEL,
            action: () => setShowEdit(false),
            className: "btn theme-btn btn-inverse me-2",
          },
          {
            text: updateLoading ? <ButtonLoader /> : BUTTON_LABEL.SAVE,
            action: handleSave,
            className: "btn theme-btn",
            isLoading: updateLoading,
          },
        ]}
        fullscreen={undefined}
        className={""}
        centerClass={""}
        modalTitleIcon={""}
      >
        <div className="form-group">
          <TextBox
            label={LABELS.STAGE_NAME_TEXT}
            id="editStageName"
            name="stageName"
            required={true}
            value={editForm.stageName}
            onChange={(e: any) =>
              setEditForm({ ...editForm, stageName: e.target.value })
            }
            placeholder="Enter Stage Name"
          />
        </div>
        <div className="form-group mt-3">
          <TextArea
            label={LABELS.TEMPLATE_BODY_TEXT}
            id="templateBody"
            name="templateBody"
            value={editForm.templateBody}
            onChange={(e: any) =>
              setEditForm({ ...editForm, templateBody: e.target.value })
            }
            maxLength={1000}
            style={{ height: "180px" }}
            rows={8}
          />
        </div>
        <div className="form-group mt-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <label className="font-weight-600 text-dark mb-0 font-14">
              Variable Mapping
            </label>
            <button
              type="button"
              className="btn theme-btn theme-btn-sm d-flex align-items-center gap-1 py-1 px-2 font-12"
              onClick={handleAddEditRow}
            >
              <i className="icon-add"></i> Add Variable
            </button>
          </div>

          <div className="p-3 border rounded bg-white shadow-sm">
            <div className="row g-2 mb-2 text-muted font-12 font-weight-600">
              <div className="col-4 d-flex align-items-center gap-1">
                <span>Interakt Variable</span>
                <span
                  title="Variable name must be the same as in the template body (e.g. {{1}}, {{2}})"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "16px",
                    height: "16px",
                    borderRadius: "50%",
                    backgroundColor: "#e9ecef",
                    color: "#495057",
                    fontSize: "11px",
                    fontWeight: "bold",
                    cursor: "help",
                  }}
                >
                  ℹ
                </span>
              </div>
              <div className="col-7">Database / Order Field</div>
              <div className="col-1 text-center">Action</div>
            </div>

            {editMappings.map((row, idx) => (
              <div key={idx} className="row g-2 align-items-center mb-2">
                <div className="col-4">
                  <input
                    type="text"
                    className="form-control"
                    style={{
                      backgroundColor: "#fff",
                      border: "1px solid #ced4da",
                      borderRadius: "4px",
                      height: "38px",
                      padding: "6px 12px",
                    }}
                    title="Variable name must be the same as in the template body (e.g. {{1}}, {{2}})"
                    placeholder={`{{${idx + 1}}}`}
                    value={row.interaktVar}
                    onChange={(e) =>
                      handleUpdateEditRow(idx, "interaktVar", e.target.value)
                    }
                  />
                </div>
                <div className="col-7">
                  <Select
                    id={`editParamSelect_${idx}`}
                    name={`editParamSelect_${idx}`}
                    value={row.dbField}
                    onChange={(e: any) =>
                      handleUpdateEditRow(idx, "dbField", e.target.value)
                    }
                    data={getStageVariables(editing?.stageKey, row.dbField)}
                    selectText="Select DB Field"
                    className="form-control select-box"
                  />
                </div>
                <div className="col-1 text-center">
                  <button
                    type="button"
                    className="btn btn-outline-danger btn-sm d-flex align-items-center justify-content-center p-0 mx-auto"
                    style={{
                      width: "38px",
                      height: "38px",
                      borderRadius: "4px",
                    }}
                    title="Delete Variable"
                    onClick={() => handleDeleteEditRow(idx)}
                    disabled={editMappings.length <= 1}
                  >
                    <i className="icon-delete font-14"></i>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="form-group mt-3">
          <TextBox
            label={LABELS.INTERAKT_TEMPLATE_NAME_TEXT}
            id="interaktTemplateName"
            name="interaktTemplateName"
            value={editForm.interaktTemplateName}
            onChange={(e: any) =>
              setEditForm({ ...editForm, interaktTemplateName: e.target.value })
            }
          />
        </div>
        <div className="form-group mt-3">
          <TextBox
            label={`${LABELS.INTERAKT_TEMPLATE_NAME_TEXT} ID`}
            id="interaktTemplateId"
            name="interaktTemplateId"
            value={editForm.interaktTemplateId}
            onChange={(e: any) =>
              setEditForm({ ...editForm, interaktTemplateId: e.target.value })
            }
          />
        </div>
      </CustomModal>

      <CustomModal
        show={showPreview}
        close={() => setShowPreview(false)}
        size={"lg"}
        message={""}
        modalTitle={LABELS.PREVIEW_TEXT}
        modalButton={[
          {
            text: BUTTON_LABEL.CLOSE,
            action: () => setShowPreview(false),
            className: "btn theme-btn",
          },
        ]}
        fullscreen={undefined}
        className={""}
        centerClass={""}
        modalTitleIcon={""}
      >
        {previewLoading ? (
          <Loader />
        ) : (
          previewData && (
            <>
              <div
                className="border rounded p-3 bg-light"
                style={{ whiteSpace: "pre-wrap" }}
              >
                {previewData.preview}
              </div>
              <div className="mt-3">
                <small className="text-muted">
                  {LABELS.CHARACTER_COUNT_TEXT}: {previewData.characterCount}
                </small>
              </div>
              {previewData.missingPlaceholders?.length > 0 && (
                <div className="mt-3 text-warning">
                  {LABELS.MISSING_PLACEHOLDERS_TEXT}:{" "}
                  {previewData.missingPlaceholders.join(", ")}
                </div>
              )}
              {previewData.needsInteraktApproval && (
                <div className="mt-3 text-danger">
                  This template is not yet mapped to an Interakt template.
                </div>
              )}
            </>
          )
        )}
      </CustomModal>
    </>
  );
};

export default WhatsAppTemplates;
